
import Vue from "vue";
import { UI } from "@/store/ui";
import { LOYALTY } from "@/store/loyalty";
import { createNamespacedHelpers } from "vuex";
import { ILoyaltyState } from "@/store/loyalty/types";
import {
  getFlyBuysMultiplier,
  getAirpointsMultiplier
} from "@/helpers/loyalty";
import { required, maxLength } from "vuelidate/lib/validators";
import { onlyDigits, date, dateGreaterThanOrEqual } from "@/validators";
import { LOYALTY_ROUTE_NAMES } from "@/loyalty-routes";
import isEqual from "lodash/isEqual";
import { campaignAccess } from "@/helpers/loyaltyEditAccess";

const { mapState } = createNamespacedHelpers(LOYALTY.NAME_SPACE);

export default Vue.extend({
  computed: {
    hasEditAccess(): boolean {
      return campaignAccess();
    },
    isLoading(): boolean {
      const { ui, loyalty } = this.$store.state;

      return ui.isloading || loyalty.isLoading;
    },
    isEditing(): boolean {
      return this.campaign && this.campaign.id;
    },
    hasStarted(): boolean {
      return (
        this.isEditing &&
        this.campaign &&
        this.campaign.startDate &&
        new Date(this.campaign.startDate) <= new Date()
      );
    },
    isFormDisabled(): boolean {
      return !this.hasEditAccess || this.isLoading || this.hasStarted;
    },
    canSave() {
      return !this.$v.$invalid && !this.isFormDisabled;
    },
    hasChanges(): boolean {
      const changes = this.$store.state.loyalty.activeCampaign;
      const original = this.$store.state.loyalty.activeCampaignOriginal;

      return !isEqual(original, changes);
    },
    airpointsMultiplier: {
      get() {
        return this.campaign && getAirpointsMultiplier(this.campaign);
      },
      set(newMultiplier: string | number) {
        this.$store.commit(
          LOYALTY.NS(LOYALTY.MUTATIONS.SET_CAMPAIGN_AIRPOINTS_MULTIPLIER),
          newMultiplier
        );
      }
    },
    flyBuysMultiplier: {
      get() {
        return this.campaign && getFlyBuysMultiplier(this.campaign);
      },
      set(newMultiplier: any) {
        this.$store.commit(
          LOYALTY.NS(LOYALTY.MUTATIONS.SET_CAMPAIGN_FLYBUYS_MULTIPLIER),
          newMultiplier
        );
      }
    },
    name: {
      get() {
        return this.campaign && this.campaign.name;
      },
      set(value: string) {
        this.$store.commit(
          LOYALTY.NS(LOYALTY.MUTATIONS.SET_CAMPAIGN_NAME),
          value
        );
      }
    },
    validFromDate: {
      get() {
        return this.campaign && this.campaign.startDate;
      },
      set(value: any) {
        this.$store.commit(
          LOYALTY.NS(LOYALTY.MUTATIONS.SET_CAMPAIGN_STARTDATE),
          value
        );

        // Re-validate validToDate because Vuelidate won't update the error
        // state if it's made valid by a change in the validFromDate.
        this.$nextTick(() => (this.$refs.validToDate as any).validate());
      }
    },
    validToDate: {
      get() {
        return this.campaign && this.campaign.endDate;
      },
      set(value: any) {
        this.$store.commit(
          LOYALTY.NS(LOYALTY.MUTATIONS.SET_CAMPAIGN_ENDDATE),
          value
        );
      }
    },
    lastUpdatedBy() {
      return this.campaign && this.campaign.lastUpdatedBy;
    },
    lastUpdated() {
      return this.campaign && this.campaign.lastUpdated;
    },
    segments: {
      get() {
        return this.campaign && this.campaign.segments;
      },
      set(value: any[]) {
        this.$store.commit(
          LOYALTY.NS(LOYALTY.MUTATIONS.SET_CAMPAIGN_SEGMENTS),
          value
        );
      }
    },
    channels: {
      get() {
        return this.campaign && this.campaign.channels;
      },
      set(value: any[]) {
        this.$store.commit(
          LOYALTY.NS(LOYALTY.MUTATIONS.SET_CAMPAIGN_CHANNELS),
          value
        );
      }
    },
    ...mapState({
      segmentList: (state: ILoyaltyState) => state.segmentList,
      channelList: (state: ILoyaltyState) => state.channelList,
      campaign: (state: ILoyaltyState) => state.activeCampaign
    }),
    minFromDateValue(): Date | undefined {
      const now = new Date();

      // Existing campaigns can use the existing date or todays date, whichever is earlier.
      if (this.isEditing) {
        const originalData = this.$store.state.loyalty.activeCampaignOriginal;

        if (originalData && originalData.startDate) {
          return originalData.startDate > now ? now : originalData.startDate;
        }
      }

      // New campaigns should not be dated in the past
      return now;
    },
    minToDateValue(): Date | undefined {
      let minDate = new Date();

      // The min to date should be after the from date
      if (this.campaign && this.campaign.validFromDate) {
        minDate = this.campaign.validFromDate;
      }

      return minDate;
    }
  },
  methods: {
    onCancelChanges() {
      const originalData = this.$store.state.loyalty.activeCampaignOriginal;
      this.$store.commit(
        LOYALTY.NS(LOYALTY.MUTATIONS.SET_CAMPAIGN),
        originalData
      );
    },
    async onSaveChanges() {
      this.$store.dispatch(LOYALTY.NS(LOYALTY.ACTIONS.TOGGLE_IS_LOADING), true);

      let success = false;

      if (this.isEditing) {
        success = await this.$store.dispatch(
          LOYALTY.NS(LOYALTY.ACTIONS.UPDATE_CAMPAIGN),
          {
            id: this.campaign.id,
            campaign: this.campaign
          }
        );
      } else {
        success = await this.$store.dispatch(
          LOYALTY.NS(LOYALTY.ACTIONS.CREATE_CAMPAIGN),
          this.campaign
        );
      }

      this.$store.dispatch(
        LOYALTY.NS(LOYALTY.ACTIONS.TOGGLE_IS_LOADING),
        false
      );

      if (success) {
        this.$router.push({
          name: LOYALTY_ROUTE_NAMES.CAMPAIGNS
        });
      }
    }
  },
  validations() {
    return {
      segments: {
        required
      },
      channels: {
        required
      },
      name: {
        required,
        maxLength: maxLength(255)
      },
      airpointsMultiplier: {
        required,
        onlyDigits
      },
      flyBuysMultiplier: {
        required,
        onlyDigits
      },
      validFromDate: {
        required,
        date
      },
      validToDate: {
        required,
        date,
        dateGreaterThanOrEqual: dateGreaterThanOrEqual("validFromDate")
      }
    };
  }
});
